import type { Camera } from '../engine/Camera';
import type { ParticleSystem } from '../effects/Particles';

/**
 * Particle renderer. Cheap by design:
 *   - One save/restore frames the whole batch (single shadowBlur set).
 *   - Each particle is a single radial-gradient disc — no glow shadow per
 *     particle (the shadowBlur is set once outside the loop).
 *   - Fades by alpha via globalAlpha (avoids per-call shadowColor parse).
 */
export class ParticleRenderer {
  draw(ctx: CanvasRenderingContext2D, camera: Camera, ps: ParticleSystem): void {
    const z = camera.zoom;
    const cx = camera.viewportW * 0.5;
    const cy = camera.viewportH * 0.5;
    const cax = camera.position.x;
    const cay = camera.position.y;
    const shakeX = camera.shakeX;
    const shakeY = camera.shakeY;

    ctx.save();
    ctx.shadowBlur = 12;

    ps.forEachAlive((p) => {
      const sx = (p.x - cax) * z + cx + shakeX;
      const sy = (p.y - cay) * z + cy + shakeY;
      const radius = Math.max(1, p.r * z * (0.6 + p.life * 0.4));
      const alpha = Math.max(0, Math.min(1, p.life));
      ctx.globalAlpha = alpha;
      ctx.shadowColor = `hsl(${p.hue} 100% 65%)`;
      const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, radius);
      g.addColorStop(0, `hsl(${p.hue} 100% 80%)`);
      g.addColorStop(1, `hsla(${p.hue} 100% 50% / 0)`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(sx, sy, radius, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.restore();
    ctx.globalAlpha = 1;
  }
}
